import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import styled, { keyframes, useTheme } from 'styled-components';
import { closeModal, openModal } from '../../redux/slices/modals';
import { PHONE_MEDIA_QUERY, useMediaQuery } from '../../common/responsive';
import {
  useDeleteCustomGiftCardDesignMutation,
  useGetCustomGiftCardDesignsQuery,
} from '../../redux/api/authorized/customGiftCardDesigns';
import { selectPrivyToken } from '../../redux/slices/auth';
import { hapticsImpactLight } from '../../common/haptic';
import { selectThemeMode } from '../../redux/slices/themeSlice';
import ChevronLeftIcon from '../../assets/icons/chevron-left.svg?react';
import ChevronRightIcon from '../../assets/icons/chevron-right.svg?react';
import Carrier from './Carrier';
import Bounceable from '../Bounceable';
import { StaggerContainer, StaggerItem } from '../Stagger';
import {
  EVENT_GIFT_FLOW_CUSTOM_IMAGE_DELETED,
  EVENT_GIFT_FLOW_IMAGE_SELECTED,
  EVENT_GIFT_FLOW_NEXT_IMAGE_SELECTED,
  EVENT_GIFT_FLOW_PREVIUS_IMAGE_SELECTED,
  sendEvent,
} from '../../common/analytics';
import { useSearchParams } from 'react-router-dom';

const CardWrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 516px;
  margin: 0 auto;
  overflow: hidden;

  @media ${PHONE_MEDIA_QUERY} {
    max-width: 407px;
  }
`;

const CardContainer = styled.div`
  width: 100%;
  aspect-ratio: 407 / 226;
  flex-shrink: 0;
  border: 1px solid ${({ theme }) => theme.border.primary};
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  padding: 12px;
  background-image: ${({ $background }) =>
    $background ? `url(${$background})` : 'none'};
  background-size: cover;
  background-position: center;

  @media ${PHONE_MEDIA_QUERY} {
    padding: 8px;
    border-radius: 8px;
  }
`;

const CardRow = styled.div``;

const TokenContainer = styled.div`
  background-color: ${({ theme }) => theme.background.secondary};
  display: flex;
  gap: 8px;
  padding: 2.5%;
  border-radius: 8px;
  align-items: center;
  width: 100%;

  @media ${PHONE_MEDIA_QUERY} {
    padding: 4px 12px;
    border-radius: 8px;
  }
`;

const TokenPrice = styled.p`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.text.title};
  @media ${PHONE_MEDIA_QUERY} {
    font-size: 14px;
  }
`;

const TokenPriceLabel = styled.p`
  font-size: 10px;
  font-weight: 400;
  color: ${({ theme }) => theme.text.secondary};
  @media ${PHONE_MEDIA_QUERY} {
    font-size: 10px;
  }
`;

const EarnsText = styled.p`
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.text.secondary};
  margin-top: 8px;
  text-align: center;
  width: 100%;
  background: ${({ theme }) => theme.background.primary};
`;

const CardAndEarnsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  width: 100%;
  background: ${({ theme }) => theme.background.primary};
`;

const TokenLogo = styled.img`
  width: 36px;
  height: 36px;
  margin-left: auto;
  flex-shrink: 0;
  @media ${PHONE_MEDIA_QUERY} {
    width: 28px;
    height: 28px;
  }
`;

const Container = styled.div`
  width: 100%;
  display: flex;
`;

const NextAndPrevious = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  width: 30px;
`;

const GiftCardImageSelectorScroll = styled.div`
  width: 100%;
  max-width: 516px;
  margin: 20px auto;
  margin-bottom: 0px;
  user-select: none;
  @media ${PHONE_MEDIA_QUERY} {
    max-width: 407px;
  }
`;

const GiftCardImageSelector = styled.div`
  display: flex;
  gap: 10px;
  width: fit-content;
  padding: 0 0 0 5px;
`;

const GiftCardImageOption = styled.div`
  width: 64px;
  height: 64px;
  padding: 2px;
  box-shadow: 0px 0px 6px 0px ${({ theme }) => theme.text.regular};
  border-radius: 999px;
  cursor: pointer;
  @media ${PHONE_MEDIA_QUERY} {
    width: 48px;
    height: 48px;
  }
  background-color: ${({ theme }) => theme.background.primary};
  border: 1px solid
    ${({ selected, theme }) =>
      selected ? theme.primary.primary : 'transparent'};
  position: relative;
`;

const GiftCardImageOptionSelected = styled.div`
  width: 100%;
  height: 100%;
  background-color: ${({ theme }) => theme.background.overlay};
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 999px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const GiftCardImageOptionImage = styled.img`
  width: 100%;
  height: 100%;
  border-radius: 999px;
  object-fit: cover;
`;

export const GiftCardEditorContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const PhysicalCardContainer = styled.div`
  aspect-ratio: 407 / 226;
  width: 100%;
  display: flex;
`;

const PhysicalCardPreviewContainer = styled.div`
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  position: relative;
  width: ${({ displayImageSelector }) =>
    displayImageSelector ? '70%' : '100%'};
`;

const PhysicalCardPreview = styled.img`
  object-fit: cover;
  height: ${({ displayImageSelector }) =>
    displayImageSelector ? '100%' : '140%'};
`;

const PhysicalCardPreviewOptionsContainer = styled.div`
  width: 30%;
  height: 100%;
  max-width: 200px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const PhysicalCardPreviewOption = styled.div`
  cursor: pointer;
  width: 100%;
  height: 100%;
  border: 1px solid
    ${({ theme, selected }) =>
      selected ? theme.primary.primary : theme.border.primary};
  border-radius: 15px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const PhysicalCardPreviewOptionImage = styled.img`
  width: 80%;
  object-fit: cover;
`;

const GiftCard = ({
  cardImage,
  token,
  usdAmount,
  tokenAmount,
  editable = false,
  onCardImageChange,
  hideValue = false,
  isPhysical = false,
  giftCardText = '',
  senderName = '',
  displayImageSelector = true,
  onCreateCustomImage,
  isModal = false,
  currentStep = 0,
  isFreeGift = false,
  lpd,
  showEarnsText = false,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [searchParams] = useSearchParams();
  const defaultImage = searchParams.get('defaultImage');
  const isValentines = defaultImage === 'valentines';

  const [selectedDesign, setSelectedDesign] = useState(0);
  const isMobile = useMediaQuery(PHONE_MEDIA_QUERY);
  const privyToken = useSelector(selectPrivyToken);
  const [selectedPhysicalCardPreview, setSelectedPhysicalCardPreview] =
    useState(0);

  const { data: customGiftCardDesigns, refetch: refetchCustomGiftCardDesigns } =
    useGetCustomGiftCardDesignsQuery({
      skip: !privyToken,
    });

  const [deleteCustomGiftCardDesign] = useDeleteCustomGiftCardDesignMutation();

  const customDesigns = customGiftCardDesigns?.customGiftCardDesigns
    ? customGiftCardDesigns?.customGiftCardDesigns
        .filter(
          (design) => design.type === (isPhysical ? 'physical' : 'digital'),
        )
        .map((design) => ({
          imageUrl: design.url,
        }))
    : [];

  let firstImage = [];

  if (lpd !== 'yield' && token?.giftCardImages) {
    if (token?.giftCardImages[0]?.imageUrl?.includes('default-giftcard.png')) {
      firstImage = [];
    } else {
      if (isPhysical) {
        const physicalImage = token?.giftCardImages?.find(
          (image) => image?.type === 'PHYSICAL',
        );
        if (physicalImage) {
          firstImage = [physicalImage];
        }
      } else {
        firstImage = [token?.giftCardImages[0]];
      }
    }
  }

  const defaultGiftCardImages = [
    ...(isValentines
      ? [
          {
            imageUrl: `${import.meta.env.VITE_FE_HOST}/${isPhysical ? 'physical/' : ''}VALENTINES.webp`,
          },
          ...firstImage,
        ]
      : [
          ...firstImage,
          {
            imageUrl: `${import.meta.env.VITE_FE_HOST}/${isPhysical ? 'physical/' : ''}VALENTINES.webp`,
          },
        ]),
    {
      imageUrl: `${import.meta.env.VITE_FE_HOST}/${isPhysical ? 'physical/' : ''}THANK_YOU.webp`,
    },
    ...(firstImage.length > 0 && isMobile
      ? []
      : [
          {
            imageUrl: `${import.meta.env.VITE_FE_HOST}/${isPhysical ? 'physical/' : ''}ANNIVERSARY.webp`,
          },
        ]),
    {
      imageUrl: `${import.meta.env.VITE_FE_HOST}/${isPhysical ? 'physical/' : ''}BIRTHDAY.webp`,
    },
  ];

  // If cardImage is a new custom image not in the list yet, add it temporarily
  const availableGiftCardImages = useMemo(() => {
    const baseImages = [...defaultGiftCardImages, ...customDesigns];

    // If cardImage exists and isn't in the list, add it (for newly saved images)
    if (cardImage) {
      const isInList = baseImages.some((img) => img?.imageUrl === cardImage);
      if (!isInList) {
        return [...baseImages, { imageUrl: cardImage }];
      }
    }

    return baseImages;
  }, [defaultGiftCardImages, customDesigns, cardImage]);

  useEffect(() => {
    if (cardImage && availableGiftCardImages?.length > 0) {
      const foundIndex = availableGiftCardImages.findIndex(
        (image) => image?.imageUrl === cardImage,
      );
      if (foundIndex !== -1) {
        setSelectedDesign(foundIndex);
      }
    }
  }, [cardImage, availableGiftCardImages]);

  useEffect(() => {
    if (
      !cardImage && // Only set default if no image is selected
      onCardImageChange &&
      availableGiftCardImages.length > 0 &&
      availableGiftCardImages[0]?.imageUrl
    ) {
      onCardImageChange(availableGiftCardImages[0].imageUrl);
    }
  }, [isPhysical, cardImage, availableGiftCardImages, onCardImageChange]);

  useEffect(() => {
    if (
      !cardImage &&
      onCardImageChange &&
      availableGiftCardImages.length > 0 &&
      availableGiftCardImages[0]?.imageUrl
    ) {
      onCardImageChange(availableGiftCardImages[0].imageUrl);
    }
  }, [cardImage, availableGiftCardImages, onCardImageChange]);

  useEffect(() => {
    if (privyToken) {
      refetchCustomGiftCardDesigns();
    }
  }, [privyToken]);

  const themeMode = useSelector(selectThemeMode);

  const theme = useTheme();

  return (
    <>
      <Container>
        {editable && !isMobile && (
          <NextAndPrevious
            onClick={() => {
              sendEvent(EVENT_GIFT_FLOW_PREVIUS_IMAGE_SELECTED, {
                is_free_gift: isFreeGift,
              });
              onCardImageChange(
                selectedDesign === 0
                  ? availableGiftCardImages[availableGiftCardImages.length - 1]
                      ?.imageUrl
                  : availableGiftCardImages[selectedDesign - 1]?.imageUrl,
              );
            }}
          >
            <ChevronLeftIcon width={9} height={48} color={theme.text.title} />
          </NextAndPrevious>
        )}
        <CardAndEarnsWrapper>
        <CardWrapper>
          {isPhysical ? (
            <PhysicalCardContainer>
              <PhysicalCardPreviewContainer
                displayImageSelector={displayImageSelector}
              >
                {selectedPhysicalCardPreview === 1 ? (
                  <PhysicalCardPreview
                    displayImageSelector={displayImageSelector}
                    src='/physical/closed.png'
                  />
                ) : (
                  <>
                    <Carrier
                      token={token}
                      cardImage={cardImage}
                      giftCardText={giftCardText}
                      senderName={senderName}
                    />
                  </>
                )}
              </PhysicalCardPreviewContainer>
              {displayImageSelector && (
                <PhysicalCardPreviewOptionsContainer>
                  <PhysicalCardPreviewOption
                    selected={selectedPhysicalCardPreview === 0}
                    onClick={() => {
                      setSelectedPhysicalCardPreview(0);
                    }}
                  >
                    <PhysicalCardPreviewOptionImage
                      src={`/physical/opened.png`}
                    />
                  </PhysicalCardPreviewOption>
                  <PhysicalCardPreviewOption
                    selected={selectedPhysicalCardPreview === 1}
                    onClick={() => {
                      setSelectedPhysicalCardPreview(1);
                    }}
                  >
                    <PhysicalCardPreviewOptionImage
                      src={`/physical/closed.png`}
                    />
                  </PhysicalCardPreviewOption>
                </PhysicalCardPreviewOptionsContainer>
              )}
            </PhysicalCardContainer>
          ) : (
            <CardContainer $background={cardImage}>
              <CardRow style={{ marginTop: 'auto' }}>
                {hideValue ? (
                  <></>
                ) : (
                  <TokenContainer>
                    <div>
                      <TokenPriceLabel>{t('giftFlow.preview.giftCardValue')}</TokenPriceLabel>
                      <TokenPrice>
                        {usdAmount ? `$${usdAmount} of` : tokenAmount}{' '}
                        {token?.symbol === 'syrupUSDC' ? 'USDC' : token?.symbol}
                      </TokenPrice>
                    </div>
                    <TokenLogo src={token?.logoURI} />
                  </TokenContainer>
                )}
              </CardRow>
            </CardContainer>
          )}
        </CardWrapper>
        {token?.symbol === 'syrupUSDC' && showEarnsText && (
          <EarnsText>{t('giftFlow.amount.earnsAutomatically')}</EarnsText>
        )}
        </CardAndEarnsWrapper>
        {editable && !isMobile && (
          <NextAndPrevious
            onClick={() => {
              sendEvent(EVENT_GIFT_FLOW_NEXT_IMAGE_SELECTED, {
                is_free_gift: isFreeGift,
              });
              onCardImageChange(
                selectedDesign === availableGiftCardImages.length - 1
                  ? availableGiftCardImages[0]?.imageUrl
                  : availableGiftCardImages[selectedDesign + 1]?.imageUrl,
              );
            }}
          >
            <ChevronRightIcon width={9} height={48} color={theme.text.title} />
          </NextAndPrevious>
        )}
      </Container>
      {editable && (
        <GiftCardImageSelectorScroll>
          <StaggerContainer>
            <GiftCardImageSelector>
              {availableGiftCardImages.map((giftCardImage, index) => (
                <StaggerItem key={index} $delay={1 + index}>
                  <Bounceable
                    fullWidth={false}
                    onClick={() => {
                      hapticsImpactLight();
                      sendEvent(EVENT_GIFT_FLOW_IMAGE_SELECTED, {
                        imageUrl: giftCardImage?.imageUrl,
                        is_free_gift: isFreeGift,
                      });
                      onCardImageChange(giftCardImage?.imageUrl);
                    }}
                  >
                    <GiftCardImageOption selected={selectedDesign === index}>
                      <GiftCardImageOptionImage
                        src={giftCardImage?.imageUrl}
                        alt='Gift Card'
                      />
                      {selectedDesign === index && (
                        <GiftCardImageOptionSelected
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent triggering parent onClick
                            hapticsImpactLight();
                            // Only allow deletion if this is a saved custom design (not a data URL)
                            const isCustomDesign = customDesigns.some(
                              (design) =>
                                design.imageUrl === giftCardImage?.imageUrl,
                            );
                            if (isCustomDesign) {
                              dispatch(
                                openModal({
                                  name: 'confirmation',
                                  props: {
                                    title: 'Delete Gift Card Design',
                                    text: 'Are you sure you want to delete this custom gift card design?',
                                    doNotClose: true,
                                    onContinue: async () => {
                                      onCardImageChange(
                                        availableGiftCardImages[0]?.imageUrl,
                                      );
                                      sendEvent(
                                        EVENT_GIFT_FLOW_CUSTOM_IMAGE_DELETED,
                                        {
                                          is_free_gift: isFreeGift,
                                        },
                                      );
                                      await deleteCustomGiftCardDesign({
                                        imageUrl: giftCardImage?.imageUrl,
                                      });
                                      dispatch(closeModal());
                                    },
                                  },
                                }),
                              );
                            }
                          }}
                        >
                          {(() => {
                            // Only show delete icon if this is a saved custom design (not a data URL)
                            const isCustomDesign = customDesigns.some(
                              (design) =>
                                design.imageUrl === giftCardImage?.imageUrl,
                            );
                            return isCustomDesign ? (
                              <img src='/delete.webp' alt='Delete' width={20} />
                            ) : null;
                          })()}
                        </GiftCardImageOptionSelected>
                      )}
                    </GiftCardImageOption>
                  </Bounceable>
                </StaggerItem>
              ))}
              <StaggerItem $delay={1 + availableGiftCardImages.length}>
                <Bounceable
                  fullWidth={false}
                  onClick={() => {
                    hapticsImpactLight();
                    if (onCreateCustomImage) {
                      onCreateCustomImage();
                    } else {
                      console.warn(
                        'GiftCard: onCreateCustomImage is required when editable={true}',
                      );
                    }
                  }}
                >
                  <GiftCardImageOption>
                    <GiftCardImageOptionImage
                      src={
                        themeMode === 'light' ? '/new.webp' : '/new-dark.webp'
                      }
                      alt='New Gift Card'
                    />
                  </GiftCardImageOption>
                </Bounceable>
              </StaggerItem>
            </GiftCardImageSelector>
          </StaggerContainer>
        </GiftCardImageSelectorScroll>
      )}
    </>
  );
};

export default GiftCard;
